/**
 * useEnquiriesState Hook
 * State management hook for the enquiries feature in collins-mobile
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDiaryContext } from "../../context/DiaryContext";
import { useUserContext } from "../../hooks/useUserContext";
import {
  EnquiriesSortOption,
  Enquiry,
  EnquiryStatus,
  FacetCounts,
  FilterOptions
} from "../../types/enquiries";
import {
  fetchEnquiries,
  takeEnquiryRequest,
  updateEnquiryStatusRequest
} from "./api";
import {
  EnquiriesUrlState,
  useEnquiriesSearchParams
} from "./useEnquiriesSearchParams";

const DEFAULT_LIMIT = 15;

const FILTERS_PREF_KEY = "enquiries_filters";
const SORT_PREF_KEY = "enquiries_sort";
const ENQUIRIES_CACHE_KEY = "enquiries_cache";
const ENQUIRIES_CACHE_TIMESTAMP_KEY = "enquiries_cache_timestamp";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface UseEnquiriesStateReturn {
  enquiries: Enquiry[];
  loading: boolean;
  error: string | null;
  filters: FilterOptions;
  sortOption: EnquiriesSortOption;
  searchQuery: string;
  facetCounts: FacetCounts | null;
  total: number;
  offset: number;
  limit: number;
  selectedEnquiry: Enquiry | null;
  recentlyUpdatedIds: string[];
  updateFilter: (key: keyof FilterOptions, value: unknown) => void;
  batchUpdateFilters: (updates: Partial<FilterOptions>) => void;
  clearFilters: () => void;
  updateSort: (sort: EnquiriesSortOption) => void;
  updateSearch: (query: string) => void;
  onPaginate: (newOffset: number) => void;
  takeEnquiry: (enquiryId: string) => Promise<void>;
  updateStatus: (enquiryId: string, status: EnquiryStatus) => Promise<void>;
  refetch: () => Promise<void>;
  setLimit: (limit: number) => void;
  setSelectedEnquiry: (enquiry: Enquiry | null) => void;
}

const getStoredValue = <T>(key: string, defaultValue: T): T => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored) as T;
    }
  } catch {
    // Ignore parsing errors
  }
  return defaultValue;
};

const setStoredValue = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
};

interface EnquiriesCache {
  enquiries: Enquiry[];
  total: number;
  facetCounts: FacetCounts | null;
}

const getCachedEnquiries = (): EnquiriesCache | null => {
  try {
    const timestamp = sessionStorage.getItem(ENQUIRIES_CACHE_TIMESTAMP_KEY);
    if (!timestamp) return null;

    const cacheAge = Date.now() - parseInt(timestamp, 10);
    if (cacheAge > CACHE_TTL_MS) {
      // Cache expired
      sessionStorage.removeItem(ENQUIRIES_CACHE_KEY);
      sessionStorage.removeItem(ENQUIRIES_CACHE_TIMESTAMP_KEY);
      return null;
    }

    const cached = sessionStorage.getItem(ENQUIRIES_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached) as EnquiriesCache;
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
};

const setCachedEnquiries = (cache: EnquiriesCache): void => {
  try {
    sessionStorage.setItem(ENQUIRIES_CACHE_KEY, JSON.stringify(cache));
    sessionStorage.setItem(
      ENQUIRIES_CACHE_TIMESTAMP_KEY,
      Date.now().toString()
    );
  } catch {
    // Ignore storage errors
  }
};

/**
 * Marks an enquiry as recently updated for visual feedback, then clears after 2 seconds
 */
const markAsRecentlyUpdated = (
  enquiryId: string,
  setRecentlyUpdatedIds: React.Dispatch<React.SetStateAction<string[]>>
): void => {
  setRecentlyUpdatedIds((prev) => [...prev, enquiryId]);
  setTimeout(() => {
    setRecentlyUpdatedIds((prev) => prev.filter((id) => id !== enquiryId));
  }, 2000);
};

export const useEnquiriesState = (): UseEnquiriesStateReturn => {
  const { venues, selectedEnquiry: contextSelectedEnquiry } = useDiaryContext();
  const { user } = useUserContext();
  const userId = user?._id;

  // Track the previous selectedEnquiry to detect changes from SelectedBooking
  const prevSelectedEnquiryRef =
    useRef<typeof contextSelectedEnquiry>(undefined);

  // Load cached data on initial mount to avoid loading state when returning from booking view
  const cachedData = useMemo(() => getCachedEnquiries(), []);

  const [enquiries, setEnquiries] = useState<Enquiry[]>(
    cachedData?.enquiries ?? []
  );
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterOptions>(() => {
    const stored = getStoredValue<FilterOptions>(FILTERS_PREF_KEY, {});
    // Default to 'me' to match collins-ui behaviour (shows "assigned to me" by default).
    // 'me' is a special value the backend interprets as the current authenticated user.
    if (!stored.assigned_to) {
      stored.assigned_to = "me";
    }
    return stored;
  });
  const [sortOption, setSortOption] = useState<EnquiriesSortOption>(() =>
    getStoredValue(SORT_PREF_KEY, "date_desc")
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [facetCounts, setFacetCounts] = useState<FacetCounts | null>(
    cachedData?.facetCounts ?? null
  );
  const [total, setTotal] = useState(cachedData?.total ?? 0);
  const [offset, setOffset] = useState(() => {
    // Initialise from URL params to prevent a race condition with
    // useEnquiriesSearchParams. When returning from SelectedBooking the URL
    // contains the saved page offset (e.g. ?start=15). Without this, offset
    // starts at 0 and the WRITE effect in useEnquiriesSearchParams overwrites
    // the URL before the READ effect can hydrate the state, causing the page
    // number to flicker and infinite API calls.
    const params = new URLSearchParams(window.location.search);
    const start = params.get("start");
    if (start) {
      const parsed = parseInt(start, 10);
      if (!Number.isNaN(parsed) && parsed >= 0) return parsed;
    }
    return 0;
  });
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [recentlyUpdatedIds, setRecentlyUpdatedIds] = useState<string[]>([]);

  // Deep linking: hydrate state from URL when params are present.
  // This callback is invoked once on mount by useEnquiriesSearchParams.
  const handleStateFromUrl = useCallback((urlState: EnquiriesUrlState) => {
    const filterUpdates: Partial<FilterOptions> = {};
    if (urlState.status) {
      filterUpdates.status = urlState.status;
    }
    if (urlState.dateFrom) {
      filterUpdates.date_from = urlState.dateFrom;
    }
    if (urlState.dateTo) {
      filterUpdates.date_to = urlState.dateTo;
    }
    if (urlState.assignedTo?.length) {
      filterUpdates.assigned_to = urlState.assignedTo.join(",");
    }
    if (Object.keys(filterUpdates).length > 0) {
      setFilters((prev) => ({ ...prev, ...filterUpdates }));
    }
    setSortOption(urlState.sort);
    setOffset(urlState.offset);
    setSearchQuery(urlState.query);
  }, []);

  // Deep linking: keep URL search params in sync with state
  useEnquiriesSearchParams({
    filters,
    sortOption,
    offset,
    searchQuery,
    onStateFromUrl: handleStateFromUrl
  });

  const venueIds = useMemo(() => venues.map((venue) => venue._id), [venues]);

  const fetchData = useCallback(
    async (showLoading = true) => {
      // Wait until userId has resolved before fetching.
      // This value arrives asynchronously after mount, so without this guard
      // it changing would recreate fetchData and re-trigger the effect,
      // causing cascading API calls on first render.
      if (!userId) return;

      // If the user has no venues assigned, set empty state immediately
      // to prevent the page from remaining in an indefinite loading or cached state.
      if (!venueIds.length) {
        setEnquiries([]);
        setTotal(0);
        setFacetCounts(null);
        setLoading(false);
        setError(null);
        return;
      }

      // Only show loading state if requested and we don't have existing data
      // This prevents the loading spinner when returning from booking view with cached data
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        // The assigned_to filter is passed directly to the Collins API as a
        // comma-separated string of user IDs. The Collins API handles this via
        // explode(',', $id) in BookingFilters.php:240, using whereIn to match
        // any of the specified users.
        const combinedFilters: FilterOptions = {
          ...filters,
          query: searchQuery || undefined
        };

        const enquiriesResponse = await fetchEnquiries({
          filters: combinedFilters,
          sort: sortOption,
          offset,
          limit,
          venueIds,
          venues
        });

        setEnquiries(enquiriesResponse.enquiries);
        setTotal(enquiriesResponse.total);
        // Use facet counts from the response - these are calculated from
        // unfiltered enquiries so sidebar counts remain accurate when filtering
        setFacetCounts(enquiriesResponse.facetCounts);

        // Cache the fetched data for quick restoration when returning from booking view
        setCachedEnquiries({
          enquiries: enquiriesResponse.enquiries,
          total: enquiriesResponse.total,
          facetCounts: enquiriesResponse.facetCounts
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch enquiries"
        );
      } finally {
        setLoading(false);
      }
    },
    [filters, sortOption, searchQuery, offset, limit, userId, venueIds, venues]
  );

  // Track if we have cached data to determine whether to show loading on initial fetch
  const hasCachedData = cachedData !== null;

  // Track whether this is the initial fetch so we can suppress the loading
  // spinner only on the very first render when cached data is available.
  // Subsequent fetches (pagination, filter / sort changes) must always show
  // the skeleton loader so users get visual feedback.
  const isInitialFetchRef = useRef(true);

  useEffect(() => {
    const showLoading = isInitialFetchRef.current ? !hasCachedData : true;
    isInitialFetchRef.current = false;
    fetchData(showLoading);
  }, [fetchData, hasCachedData]);

  useEffect(() => {
    setStoredValue(FILTERS_PREF_KEY, filters);
  }, [filters]);

  useEffect(() => {
    setStoredValue(SORT_PREF_KEY, sortOption);
  }, [sortOption]);

  // Sync enquiries list when selectedEnquiry is updated from SelectedBooking modal
  // This ensures the EnquiryCard icon updates when Take button is clicked in the modal
  useEffect(() => {
    if (
      contextSelectedEnquiry &&
      prevSelectedEnquiryRef.current?._id === contextSelectedEnquiry._id &&
      prevSelectedEnquiryRef.current?.status !== contextSelectedEnquiry.status
    ) {
      // The same enquiry was updated (e.g., status changed from 'new' to 'in_progress')
      setEnquiries((prev) =>
        prev.map((e) =>
          e._id === contextSelectedEnquiry._id
            ? { ...e, ...contextSelectedEnquiry }
            : e
        )
      );
      markAsRecentlyUpdated(contextSelectedEnquiry._id, setRecentlyUpdatedIds);
    }
    prevSelectedEnquiryRef.current = contextSelectedEnquiry;
  }, [contextSelectedEnquiry]);

  const updateFilter = useCallback(
    (key: keyof FilterOptions, value: unknown) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value
      }));
      setOffset(0);
    },
    []
  );

  /**
   * Apply multiple filter changes in a single batched setState call.
   * This prevents intermediate renders and potential race conditions
   * with debounced API calls when clearing or adjusting several filters
   * at once (e.g. clearing both date_from and date_to simultaneously).
   */
  const batchUpdateFilters = useCallback((updates: Partial<FilterOptions>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
    setOffset(0);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setOffset(0);
  }, []);

  const updateSort = useCallback((sort: EnquiriesSortOption) => {
    setSortOption(sort);
    setOffset(0);
  }, []);

  const updateSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setOffset(0);
  }, []);

  const onPaginate = useCallback((newOffset: number) => {
    setOffset(newOffset);
  }, []);

  const takeEnquiry = useCallback(
    async (enquiryId: string) => {
      if (!userId) {
        setError("User ID not available");
        return;
      }
      try {
        const updatedEnquiry = await takeEnquiryRequest(enquiryId, userId);
        setEnquiries((prev) =>
          prev.map((e) =>
            e._id === enquiryId ? { ...e, ...updatedEnquiry } : e
          )
        );
        markAsRecentlyUpdated(enquiryId, setRecentlyUpdatedIds);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to take enquiry");
      }
    },
    [userId]
  );

  const updateStatus = useCallback(
    async (enquiryId: string, status: EnquiryStatus) => {
      try {
        const updatedEnquiry = await updateEnquiryStatusRequest(
          enquiryId,
          status
        );
        setEnquiries((prev) =>
          prev.map((e) =>
            e._id === enquiryId ? { ...e, ...updatedEnquiry } : e
          )
        );
        markAsRecentlyUpdated(enquiryId, setRecentlyUpdatedIds);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to update status"
        );
      }
    },
    []
  );

  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  return useMemo(
    () => ({
      enquiries,
      loading,
      error,
      filters,
      sortOption,
      searchQuery,
      facetCounts,
      total,
      offset,
      limit,
      selectedEnquiry,
      recentlyUpdatedIds,
      updateFilter,
      batchUpdateFilters,
      clearFilters,
      updateSort,
      updateSearch,
      onPaginate,
      takeEnquiry,
      updateStatus,
      refetch,
      setLimit,
      setSelectedEnquiry
    }),
    [
      enquiries,
      loading,
      error,
      filters,
      sortOption,
      searchQuery,
      facetCounts,
      total,
      offset,
      limit,
      selectedEnquiry,
      recentlyUpdatedIds,
      updateFilter,
      batchUpdateFilters,
      clearFilters,
      updateSort,
      updateSearch,
      onPaginate,
      takeEnquiry,
      updateStatus,
      refetch,
      setSelectedEnquiry
    ]
  );
};
